import React, { useContext, useState } from "react";
import { Modal, Button, Card } from "antd";
import {
  EyeOutlined,
  CloseOutlined,
  AppleFilled,
  GoogleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import axios from "axios";
import { BASE_URL, RoleEnum } from "../../Constants";

const CardPreview = ({
  id,
  name,
  isOpen,
  onClose,
  cardData,
  isIndividual,
  loading = false,
  isArabic: isArabicOverride,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useState("apple");
  const [btnLoading, setBtnLoading] = useState({
    apple: false,
    samsung: false,
    google: false,
  });
  const authCtx = useContext(AppContext);
  const isArabic = isArabicOverride ?? i18n.language === "ar";

  function getContrastColor(hex) {
    // Remove #
    hex = hex.replace("#", "");

    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // If luminance is high → return black, else white
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  const getTextStyle = (field) => {
    const defaultStyles = {
      color: "#ffffff",
    };

    const fieldStyles = cardData?.textStyles?.[field] || defaultStyles;

    return {
      color: fieldStyles.color || defaultStyles.color,
    };
  };

  function getValueColor(cardConfig) {
    let valueColor = "#FFFFFF"; // default

    if (cardConfig?.textStyles) {
      const firstValidEntry = Object.entries(cardConfig.textStyles).find(
        ([key, value]) =>
          key !== "titleColor" && value?.color && value.color.trim() !== ""
      );
      if (firstValidEntry) {
        valueColor = firstValidEntry[1].color;
      }
    }

    return valueColor;
  }

  const renderCard = () => {
    if (selectedDevice === "apple") {
      const textColor = getValueColor(cardData);
      return (
        <div
          style={{
            backgroundColor: !cardData?.useBackgroundImage
              ? cardData?.backgroundColor || "#0a0a52"
              : "#0a0a52",
            backgroundImage:
              cardData?.useBackgroundImage &&
              (cardData?.backgroundImagePreview || cardData?.backgroundImageUrl)
                ? `url(${
                    cardData?.backgroundImagePreview ||
                    cardData?.backgroundImageUrl
                  })`
                : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="apple-pass-card w-full max-w-[330px] sm:max-w-[360px] h-auto min-h-[380px] sm:min-h-[450px] sm:h-[500px] py-2 px-1 rounded-none text-white shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex flex-col justify-between items-center pl-2 pr-3 sm:pr-5">
            {cardData?.companyLogoVisible && (
              <img
                src={cardData?.companyLogoUrl || "/future_cards_logo.png"}
                alt="Future Cards"
                className="h-8 sm:h-10 self-start"
                onError={(e) => {
                  e.target.src = "/future_cards_logo.png";
                }}
              />
            )}
            <div className="flex justify-between w-full">
              <div className="self-end">
                {cardData?.nameVisible && cardData?.name && (
                  <div className="px-1 sm:px-2">
                    <p style={getTextStyle("titleColor")} className="text-[10px] sm:text-xs">
                      NAME
                    </p>
                    <p
                      style={{ color: textColor }}
                      className="text-sm sm:text-base font-semibold"
                    >
                      {cardData?.name}
                    </p>
                  </div>
                )}
                {cardData?.designationVisible && cardData?.designation && (
                  <div className="px-1 sm:px-2 mt-1 sm:mt-2">
                    <p style={getTextStyle("titleColor")} className="text-[10px] sm:text-xs">
                      Designation
                    </p>
                    <p style={{ color: textColor }} className="text-sm sm:text-base">
                      {cardData?.designation}
                    </p>
                  </div>
                )}
              </div>
              {cardData?.profileImageVisible !== false && (
                <img
                  src={
                    cardData?.bgRemovedProfileUrl ||
                    cardData?.profileImagePreview ||
                    (cardData?.gender === "Female" ? `/user_placeholder_women.png` : `/user-placeholder.png`)
                  }
                  alt="Profile"
                  className="w-16 h-16 sm:w-24 sm:h-24 object-cover"
                  onError={(e) => {
                    e.target.src = cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png";
                  }}
                />
              )}
            </div>
          </div>

          <div className="px-3 sm:px-5 py-2 sm:py-4 space-y-2 sm:space-y-4 h-full">
            {cardData?.companyNameVisible && cardData?.companyName && (
              <div>
                <p style={getTextStyle("titleColor")} className="text-[10px] sm:text-xs">
                  COMPANY
                </p>
                <p style={{ color: textColor }} className="text-sm sm:text-base">
                  {cardData?.companyName}
                </p>
              </div>
            )}

            <div
              className="flex justify-between text-[10px] sm:text-xs"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))",
                maxWidth: "100%",
              }}
            >
              {cardData?.emailVisible && cardData?.email && (
                <div>
                  <p style={getTextStyle("titleColor")} className="text-[10px] sm:text-xs">
                    EMAIL
                  </p>
                  <p style={{ color: textColor }} className="break-words text-[10px] sm:text-xs">
                    {cardData?.email}
                  </p>
                </div>
              )}
              {cardData?.phoneVisible && cardData?.phone && (
                <div>
                  <p
                    style={getTextStyle("titleColor")}
                    className="text-xs text-start"
                  >
                    PHONE
                  </p>
                  <p style={{ color: textColor }}>{cardData?.phone}</p>
                </div>
              )}
              {cardData?.employeeIdVisible && cardData?.employeeId && (
                <div>
                  <p style={getTextStyle("titleColor")} className="text-xs">
                    EMPLOYEE ID
                  </p>
                  <p style={{ color: textColor }}>{cardData?.employeeId}</p>
                </div>
              )}
            </div>

            {cardData?.qrCodeVisible && cardData?.qrCodeUrl && (
              <div className="flex justify-center items-end h-1/3 mt-20">
                <img
                  src={cardData.qrCodeUrl}
                  alt="QR Code"
                  width="100px"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
          </div>
        </div>
      );
    } else if (selectedDevice === "google") {
      const textColor = getContrastColor(
        cardData?.backgroundColor || "#1e1b5c"
      );
      return (
        <div
          style={{
            backgroundColor: !cardData?.useBackgroundImage
              ? cardData?.backgroundColor || "#1e1b5c"
              : undefined,
            backgroundImage:
              cardData?.useBackgroundImage &&
              (cardData?.backgroundImagePreview || cardData?.backgroundImageUrl)
                ? `url(${
                    cardData?.backgroundImagePreview ||
                    cardData?.backgroundImageUrl
                  })`
                : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="w-full max-w-[340px] sm:max-w-[380px] h-fit rounded-lg shadow-lg overflow-hidden relative text-white"
        >
          {/* Header with Company Logo */}
          <div className="p-6 pb-0">
            {cardData?.companyLogoVisible && (
              <img
                src={cardData?.companyLogoUrl || "/future_cards_logo.png"}
                alt="Company Logo"
                className="h-10 object-contain"
                onError={(e) => {
                  e.target.src = "/future_cards_logo.png";
                }}
              />
            )}
          </div>

          <hr className="border-t border-gray-300 my-1 mx-2" />

          {/* Main Content */}
          <div className="px-6 py-4 flex-1">
            {/* Name and Title Section */}
            <div className="mb-6">
              {cardData?.designationVisible && cardData?.designation && (
                <h2 style={{ color: textColor }} className="text-sm">
                  {cardData?.designation}
                </h2>
              )}
              {cardData?.nameVisible && cardData?.name && (
                <p style={{ color: textColor }} className="text-xl mb-1">
                  {cardData?.name}
                </p>
              )}
            </div>

            {/* Contact Information Grid */}
            <div className="space-y-4 mb-6">
              {/* Phone and Employee ID in a row */}
              <div className="flex justify-between">
                {cardData?.companyNameVisible && cardData?.companyName && (
                  <div className="flex-1 mr-4">
                    <p
                      style={{ color: textColor }}
                      className="text-sm opacity-80 mb-1"
                    >
                      Company
                    </p>
                    <p style={{ color: textColor }} className="text-base">
                      {cardData?.companyName}
                    </p>
                  </div>
                )}

                {cardData?.employeeIdVisible && cardData?.employeeId && (
                  <div className="text-right">
                    <p
                      style={{ color: textColor }}
                      className="text-sm opacity-80 mb-1"
                    >
                      Employee ID
                    </p>
                    <p style={{ color: textColor }} className="text-base">
                      {cardData?.employeeId}
                    </p>
                  </div>
                )}
              </div>

              <hr className="border-t border-gray-300 my-1" />

              {/* Email */}
              <div className="flex justify-between">
                {cardData?.phoneVisible && cardData?.phone && (
                  <div className="flex-1 mr-4">
                    <p
                      style={{ color: textColor }}
                      className="text-sm opacity-80 mb-1"
                    >
                      Phone
                    </p>
                    <p style={{ color: textColor }} className="text-base">
                      {cardData?.phone}
                    </p>
                  </div>
                )}
                {cardData?.emailVisible && cardData?.email && (
                  <div>
                    <p
                      style={{ color: textColor }}
                      className="text-sm opacity-80 mb-1 text-right"
                    >
                      Email
                    </p>
                    <p
                      style={{ color: textColor }}
                      className="text-base break-all"
                    >
                      {cardData?.email}
                    </p>
                  </div>
                )}{" "}
              </div>
            </div>
          </div>

          {/* QR Code */}
          {cardData?.qrCodeVisible && cardData?.qrCodeUrl && (
            <div className="flex justify-center px-6 pb-6">
              <div className="bg-white p-4 rounded-lg">
                <img
                  src={cardData?.qrCodeUrl}
                  alt="QR Code"
                  className="w-24 h-24"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            </div>
          )}

          {/* Profile Image - Bottom Right */}
          {cardData?.profileImageVisible !== false && (
            <div className="">
              <img
                src={cardData?.profileImagePreview || (cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png")}
                alt="Profile"
                className="w-full object-cover"
                onError={(e) => {
                  e.target.src = cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png";
                }}
              />
            </div>
          )}
        </div>
      );
    }

    // Default render for Samsung
    return (
      <div
        style={{
          backgroundColor: !cardData?.useBackgroundImage
            ? cardData?.backgroundColor || "#ffffff"
            : "#ffffff",
          backgroundImage:
            cardData?.useBackgroundImage &&
            (cardData?.backgroundImagePreview || cardData?.backgroundImageUrl)
              ? `url(${
                  cardData?.backgroundImagePreview ||
                  cardData?.backgroundImageUrl
                })`
              : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="w-full max-w-[320px] sm:max-w-[350px] h-fit bg-white rounded-xl shadow-lg overflow-hidden relative border border-gray-200"
      >
        {/* Header Section with Company Logo */}
        <div
          className="h-20 flex items-center justify-between px-6"
          style={{
            backgroundColor: cardData?.backgroundColor || "#f8f9fa",
            borderBottom: `2px solid ${cardData?.dividerColor || "#e9ecef"}`,
          }}
        >
          {cardData?.companyLogoVisible && (
            <img
              src={cardData?.companyLogoUrl || "/future_cards_logo.png"}
              alt="Company Logo"
              className="h-12 object-contain"
              onError={(e) => {
                e.target.src = "/future_cards_logo.png";
              }}
            />
          )}
          <div className="text-right">
            {cardData?.companyNameVisible && cardData?.companyName && (
              <p
                style={getTextStyle("companyName")}
                className="text-lg font-semibold text-gray-800"
              >
                {cardData?.companyName}
              </p>
            )}
          </div>
        </div>

        {/* Profile Section */}
        <div className="px-6 py-6 text-center">
          {cardData?.profileImageVisible !== false && (
            <div className="mb-4">
              <img
                src={cardData?.profileImagePreview || (cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png")}
                alt="Profile"
                className="w-24 h-24 object-cover shadow-md"
                onError={(e) => {
                  e.target.src = cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png";
                }}
              />
            </div>
          )}

          {cardData?.nameVisible && cardData?.name && (
            <h2
              style={getTextStyle("name")}
              className="text-2xl font-bold text-gray-900 mb-2"
            >
              {cardData?.name}
            </h2>
          )}

          {cardData?.designationVisible && cardData?.designation && (
            <p
              style={getTextStyle("designation")}
              className="text-lg text-gray-600 mb-4"
            >
              {cardData?.designation}
            </p>
          )}
        </div>

        {/* Contact Information Section */}
        <div className="px-6 space-y-4">
          {cardData?.phoneVisible && cardData?.phone && (
            <div className="flex items-center p-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 text-lg">📱</span>
              </div>
              <div className="flex-1">
                <p
                  style={getTextStyle("titleColor")}
                  className="text-sm font-medium text-gray-500 uppercase tracking-wide"
                >
                  Phone
                </p>
                <p
                  style={getTextStyle("phone")}
                  className="text-base font-medium text-gray-900"
                >
                  {cardData?.phone}
                </p>
              </div>
            </div>
          )}

          {cardData?.emailVisible && cardData?.email && (
            <div className="flex items-center p-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-green-600 text-lg">📧</span>
              </div>
              <div className="flex-1">
                <p
                  style={getTextStyle("titleColor")}
                  className="text-sm font-medium text-gray-500 uppercase tracking-wide"
                >
                  Email
                </p>
                <p
                  style={getTextStyle("email")}
                  className="text-base font-medium text-gray-900 break-all"
                >
                  {cardData?.email}
                </p>
              </div>
            </div>
          )}

          {cardData?.employeeIdVisible && cardData?.employeeId && (
            <div className="flex items-center p-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-purple-600 text-lg">🆔</span>
              </div>
              <div className="flex-1">
                <p
                  style={getTextStyle("titleColor")}
                  className="text-sm font-medium text-gray-500 uppercase tracking-wide"
                >
                  Employee ID
                </p>
                <p
                  style={getTextStyle("employeeId")}
                  className="text-base font-medium text-gray-900"
                >
                  {cardData?.employeeId}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* QR Code Section */}
        {cardData?.qrCodeVisible && cardData?.qrCodeUrl && (
          <div className="flex justify-center px-6 py-6">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-sm">
              <img
                src={cardData?.qrCodeUrl}
                alt="QR Code"
                className="w-20 h-20"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div
          className="absolute bottom-0 left-0 right-0 h-2"
          style={{
            backgroundColor: cardData?.backgroundColor || "#f8f9fa",
          }}
        />
      </div>
    );
  };

  const handleGeneratePass = async () => {
    setBtnLoading({ ...btnLoading, apple: true });
    try {
      // iOS Safari only hands the response to the native "Add to Wallet" sheet when it sees
      // a real navigation to a .pkpass response, not a synthetic blob: URL + <a download> click.
      const passUrl = `${BASE_URL}/wallet/apple/generate-pass?employeeId=${id}&lng=${
        isArabic ? "ar" : "en"
      }&isIndividual=${isIndividual}`;
      window.location.href = passUrl;
      setTimeout(() => {
        setBtnLoading({ ...btnLoading, apple: false });
      }, 10000);
    } catch (error) {
      console.error("Error:", error);
      setBtnLoading({ ...btnLoading, apple: false });
    }
  };

  const addToSamsungWallet = async () => {
    setBtnLoading({ ...btnLoading, samsung: true });

    try {
      const response = await axios.post(
        `${BASE_URL}/wallet/samsung/generate-jwt?employeeId=${id}&isIndividual=${isIndividual}&lng=${
          isArabic ? "ar" : "en"
        }`
      );

      if (response.data.walletUrl) {
        window.location.href = response.data.walletUrl;
      } else {
        alert(t("messages.error.samsungWalletFailed"));
      }
    } catch (error) {
      console.error("Error adding to Samsung Wallet:", error);
      alert(t("messages.error.samsungWalletAddFailed"));
    }
    setBtnLoading({ ...btnLoading, samsung: false });
  };

  const addToGWallet = async () => {
    setBtnLoading({ ...btnLoading, google: true });
    try {
      const response = await axios.post(
        `${BASE_URL}/wallet/google/generate-jwt?employeeId=${id}&isIndividual=${isIndividual}&lng=${
          isArabic ? "ar" : "en"
        }`
      );

      if (response.data.walletUrl) {
        window.location.href = response.data.walletUrl;
      } else {
        alert(t("messages.error.googleWalletFailed"));
      }
    } catch (error) {
      console.error("Error adding to Google Wallet:", error);
      alert(t("messages.error.googleWalletAddFailed"));
    }
    setBtnLoading({ ...btnLoading, google: false });
  };

  return (
    <Modal
      title={
        <div className="flex items-center text-sm sm:text-base">
          <EyeOutlined className="mr-2" />
          <span className="truncate">{t("preview.cardPreview")} - {name}</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width="100%"
      style={{ maxWidth: '800px', top: 20, margin: '0 auto' }}
      className="card-preview-modal"
    >
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-6">
        {/* Device Selection */}
        <div className="w-full lg:w-1/3">
          <h4 className="text-sm sm:text-lg font-semibold mb-2 sm:mb-3">
            {t("preview.selectDevice")}
          </h4>
          <div className="flex lg:flex-col gap-1.5 sm:gap-2 overflow-x-auto lg:overflow-x-visible pb-1 sm:pb-2 lg:pb-0">
            <button
              onClick={() => setSelectedDevice("apple")}
              className={`flex-1 lg:w-full text-left p-2 sm:p-3 rounded-lg border transition-all min-w-[90px] sm:min-w-[100px] ${
                selectedDevice === "apple"
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-200 hover:border-gray-300 active:bg-gray-50"
              }`}
            >
              <div className="flex lg:flex-row flex-col items-center lg:items-center gap-2">
                <div className="w-8 h-8 bg-white border rounded-lg flex items-center justify-center flex-shrink-0">
                  <AppleFilled height={14} color="#000" />
                </div>
                <div className="text-center lg:text-left">
                  <p className="font-medium text-xs sm:text-sm whitespace-nowrap">
                    {t("cardPreview.wallets.apple")}
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedDevice("samsung")}
              className={`flex-1 lg:w-full text-left p-2.5 sm:p-3 rounded-lg border transition-all min-w-[100px] ${
                selectedDevice === "samsung"
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-200 hover:border-gray-300 active:bg-gray-50"
              }`}
            >
              <div className="flex lg:flex-row flex-col items-center lg:items-center gap-2">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <img
                    src="https://us-cdn-gpp.mcsvc.samsung.com/lib/wallet-card.png"
                    alt=""
                    className="w-6 h-6"
                  />
                </div>
                <div className="text-center lg:text-left">
                  <p className="font-medium text-xs sm:text-sm whitespace-nowrap">
                    {t("cardPreview.wallets.samsung")}
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedDevice("google")}
              className={`flex-1 lg:w-full text-left p-2.5 sm:p-3 rounded-lg border transition-all min-w-[100px] ${
                selectedDevice === "google"
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-gray-200 hover:border-gray-300 active:bg-gray-50"
              }`}
            >
              <div className="flex lg:flex-row flex-col items-center lg:items-center gap-2">
                <div className="w-8 h-8 bg-white border border-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
                  <GoogleOutlined style={{ fontSize: 20, color: "#4285F4" }} />
                </div>
                <div className="text-center lg:text-left">
                  <p className="font-medium text-xs sm:text-sm whitespace-nowrap">
                    {t("cardPreview.wallets.google")}
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-4 lg:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg hidden sm:block">
            <h5 className="font-medium mb-2 text-sm">{t("preview.previewInfo")}</h5>
            <p className="text-xs sm:text-sm text-gray-600">
              {t("preview.previewDescription")}
            </p>
          </div>

          {authCtx.profile.role === RoleEnum.PUBLIC_EMPLOYEE &&
          id &&
          selectedDevice === "apple" ? (
            <div className="mt-3 lg:mt-4">
              <button
                onClick={handleGeneratePass}
                disabled={btnLoading.apple}
                className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium w-full bg-black text-white hover:bg-gray-800 active:bg-gray-900 disabled:bg-gray-400 rounded-lg shadow-md transition-colors text-sm sm:text-base"
              >
                {btnLoading.apple && (
                  <LoadingOutlined style={{ color: "white" }} />
                )}
                <AppleFilled height={14} />
                <span>{t("cardPreview.buttons.addToAppleWallet")}</span>
              </button>
            </div>
          ) : authCtx.profile.role === RoleEnum.PUBLIC_EMPLOYEE &&
            id &&
            selectedDevice === "google" ? (
            <div className="mt-3 lg:mt-4">
              <button
                onClick={addToGWallet}
                disabled={btnLoading.google}
                className="flex h-10 sm:h-12 px-2 sm:px-3 items-center justify-center w-full gap-2 bg-black hover:bg-gray-800 active:bg-gray-900 disabled:bg-gray-400 rounded-lg shadow-md transition-colors"
              >
                {btnLoading.google && (
                  <LoadingOutlined style={{ color: "white" }} />
                )}
                <img
                  src="/g_wallet-btn.svg"
                  className="max-h-8 w-full bg-transparent max-w-[150px] object-contain"
                  alt={t("cardPreview.buttons.addToGoogleWallet")}
                />
              </button>
            </div>
          ) : (
            authCtx.profile.role === RoleEnum.PUBLIC_EMPLOYEE &&
            id && (
              <div className="mt-3 lg:mt-4">
                <button
                  onClick={addToSamsungWallet}
                  disabled={btnLoading.samsung}
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium w-full bg-black text-white hover:bg-gray-800 active:bg-gray-900 disabled:bg-gray-400 rounded-lg shadow-md transition-colors text-sm sm:text-base"
                >
                  {btnLoading.samsung && (
                    <LoadingOutlined style={{ color: "white" }} />
                  )}
                  <img
                    src="https://us-cdn-gpp.mcsvc.samsung.com/lib/wallet-card.png"
                    alt="Samsung Wallet"
                    className="w-6 h-6"
                  />
                  <span>{t("cardPreview.buttons.addToSamsungWallet")}</span>
                  <img
                    src="https://us-rd.mcsvc.samsung.com/statistics/impression/addtowlt?ep=C50C3754FEB24833B30C10B275BB6AB8;cc=GC;ii=1287098641027170318;co=4111756569175138240;cp=1288017491089625089;si=24;pg=101212967375212546;pi=Aqz68EBXSx6Mv9jsaZxzaA;tp=4111774992902138240;li=0"
                    style={{ width: "1px", height: "1px" }}
                    alt=""
                  />
                </button>
              </div>
            )
          )}
        </div>

        {/* Card Preview */}
        <div className="w-full lg:w-2/3 overflow-auto max-h-[55vh] sm:max-h-[60vh] lg:h-[70vh] flex justify-center items-start relative bg-gray-50 rounded-lg p-0 sm:p-3 lg:p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full w-full">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
              <p className="text-gray-600 text-base sm:text-lg">
                {t("cardPreview.loading.title")}
              </p>
              <p className="text-gray-400 text-xs sm:text-sm mt-2">
                {t("cardPreview.loading.description")}
              </p>
            </div>
          ) : (
            renderCard()
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CardPreview;
